import dayjs from "dayjs";

import { LOCAL_STORAGE_KEY } from "../../constants";

export const getToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) || null;
};

export const formatMoney = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  if (value === 0) return "0円";
  return `${new Intl.NumberFormat("ja-JP").format(value)}円`;
};

/**
 * Format number to Japanese locale
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat("ja-JP").format(num);
};

/**
 * Converts hiragana characters to katakana characters
 * @param text - The input text containing hiragana characters
 * @returns The text with hiragana characters converted to katakana
 */
export const convertHiraganaToKatakana = (text: string): string => {
  return text.replace(/[\u3041-\u3096]/g, (char) => {
    // Convert hiragana (U+3041-U+3096) to katakana (U+30A1-U+30F6)
    // The difference is 0x60 (96 in decimal)
    return String.fromCharCode(char.charCodeAt(0) + 0x60);
  });
};

/**
 * Converts half-width katakana characters to full-width katakana characters
 * @param text - The input text containing half-width katakana characters
 * @returns The text with half-width katakana characters converted to full-width katakana
 */
export const convertHalfWidthKatakanaToFullWidth = (text: string): string => {
  // Half-width katakana to full-width katakana mapping
  const halfToFullMap: { [key: string]: string } = {
    ｦ: "ヲ",
    ｧ: "ァ",
    ｨ: "ィ",
    ｩ: "ゥ",
    ｪ: "ェ",
    ｫ: "ォ",
    ｬ: "ャ",
    ｭ: "ュ",
    ｮ: "ョ",
    ｯ: "ッ",
    ｰ: "ー",
    ｱ: "ア",
    ｲ: "イ",
    ｳ: "ウ",
    ｴ: "エ",
    ｵ: "オ",
    ｶ: "カ",
    ｷ: "キ",
    ｸ: "ク",
    ｹ: "ケ",
    ｺ: "コ",
    ｻ: "サ",
    ｼ: "シ",
    ｽ: "ス",
    ｾ: "セ",
    ｿ: "ソ",
    ﾀ: "タ",
    ﾁ: "チ",
    ﾂ: "ツ",
    ﾃ: "テ",
    ﾄ: "ト",
    ﾅ: "ナ",
    ﾆ: "ニ",
    ﾇ: "ヌ",
    ﾈ: "ネ",
    ﾉ: "ノ",
    ﾊ: "ハ",
    ﾋ: "ヒ",
    ﾌ: "フ",
    ﾍ: "ヘ",
    ﾎ: "ホ",
    ﾏ: "マ",
    ﾐ: "ミ",
    ﾑ: "ム",
    ﾒ: "メ",
    ﾓ: "モ",
    ﾔ: "ヤ",
    ﾕ: "ユ",
    ﾖ: "ヨ",
    ﾗ: "ラ",
    ﾘ: "リ",
    ﾙ: "ル",
    ﾚ: "レ",
    ﾛ: "ロ",
    ﾜ: "ワ",
    ﾝ: "ン",
    ﾞ: "゛", // Dakuten
    ﾟ: "゜" // Handakuten
  };

  // Handle half-width katakana with dakuten/handakuten
  // e.g., ｶﾞ -> ガ, ﾊﾟ -> パ
  const dakutenMap: { [key: string]: string } = {
    ｶﾞ: "ガ",
    ｷﾞ: "ギ",
    ｸﾞ: "グ",
    ｹﾞ: "ゲ",
    ｺﾞ: "ゴ",
    ｻﾞ: "ザ",
    ｼﾞ: "ジ",
    ｽﾞ: "ズ",
    ｾﾞ: "ゼ",
    ｿﾞ: "ゾ",
    ﾀﾞ: "ダ",
    ﾁﾞ: "ヂ",
    ﾂﾞ: "ヅ",
    ﾃﾞ: "デ",
    ﾄﾞ: "ド",
    ﾊﾞ: "バ",
    ﾋﾞ: "ビ",
    ﾌﾞ: "ブ",
    ﾍﾞ: "ベ",
    ﾎﾞ: "ボ",
    ﾊﾟ: "パ",
    ﾋﾟ: "ピ",
    ﾌﾟ: "プ",
    ﾍﾟ: "ペ",
    ﾎﾟ: "ポ",
    ｳﾞ: "ヴ"
  };

  let result = text;

  // First, convert combined characters (with dakuten/handakuten)
  Object.keys(dakutenMap).forEach((half) => {
    const regex = new RegExp(half.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    result = result.replace(regex, dakutenMap[half]);
  });

  // Then, convert remaining half-width katakana characters
  return result.replace(/[\uFF66-\uFF9F]/g, (char) => {
    return halfToFullMap[char] || char;
  });
};

/**
 * Normalize kana input for form fields:
 * - half-width katakana -> full-width katakana
 * - hiragana -> katakana
 * - half-width spaces -> full-width spaces
 */
export const normalizeKanaInput = (text: string): string => {
  if (!text) return "";

  let normalized = convertHalfWidthKatakanaToFullWidth(text);
  normalized = convertHiraganaToKatakana(normalized);

  return normalized.normalize("NFKC").replace(/ /g, "　");
};

/**
 * Utility function to format postcode from 7 digits to XXX-XXXX format
 * @param postcode - The postcode string to format
 * @returns Formatted postcode string (XXX-XXXX format) or empty string
 */
export const formatPostcode = (postcode: string): string => {
  if (!postcode) return "";
  // Remove all non-digit characters
  const digitsOnly = postcode.replace(/\D/g, "");
  // If it's exactly 7 digits, format as XXX-XXXX
  if (digitsOnly.length === 7) {
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
  }
  // If it already has the correct format (XXX-XXXX), return as is
  if (/^\d{3}-\d{4}$/.test(postcode.trim())) {
    return postcode.trim();
  }
  // Otherwise, return trimmed value
  return postcode.trim();
};

/**
 * Removes leading zeros from numeric strings
 * Used for sanitizing numeric input before sending to API
 * @param value - The numeric string value (e.g., "09", "000123")
 * @returns The sanitized string without leading zeros (e.g., "9", "123")
 * @example
 * removeLeadingZeros("09") // returns "9"
 * removeLeadingZeros("000123") // returns "123"
 * removeLeadingZeros("0") // returns "0"
 * removeLeadingZeros("") // returns ""
 * removeLeadingZeros("abc") // returns "abc" (non-numeric values preserved for validation)
 */
export const removeLeadingZeros = (value: string | undefined): string => {
  if (!value) return "";
  const trimmedValue = value.trim();
  if (trimmedValue === "") return "";
  // Convert to number to remove leading zeros, then back to string
  const numValue = Number(trimmedValue);
  // If not a valid number, return trimmed value (validation should catch this)
  if (isNaN(numValue)) return trimmedValue;
  // Return as string, preserving "0" if the value is actually zero
  return numValue.toString();
};

/**
 * Recursively trims all string values in an object or array
 * Used for sanitizing request data before sending to API
 * @param data - The data to trim (object, array, or primitive)
 * @returns The trimmed data
 * @example
 * trimData({ name: "  john  ", rent: "  0123  " }) // returns { name: "john", rent: "0123" }
 * trimData(["  hello  ", "  world  "]) // returns ["hello", "world"]
 * trimData("  test  ") // returns "test"
 * trimData("  0123  ") // returns "0123"
 */
export const trimData = (data: any): any => {
  // Handle null or undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle FormData - don't trim FormData
  if (data instanceof FormData) {
    return data;
  }

  // Handle string - trim it
  if (typeof data === "string") {
    return data.trim();
  }

  // Handle array - recursively trim each element
  if (Array.isArray(data)) {
    return data.map((item) => trimData(item));
  }

  // Handle object - recursively trim each property
  if (typeof data === "object") {
    const trimmed: any = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        trimmed[key] = trimData(data[key]);
      }
    }
    return trimmed;
  }

  // Handle other primitives (number, boolean, etc.) - return as is
  return data;
};

export const isJapanese = (text: string) => {
  const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
  return japaneseRegex.test(text);
};

export const shortenFileName = (name: string, maxLength = 25) => {
  if (!name) return "";

  const dotIndex = name.lastIndexOf(".");
  if (dotIndex === -1) {
    return name.length > maxLength ? name.slice(0, maxLength - 4) + "..." : name;
  }

  const ext = name.slice(dotIndex);
  const base = name.slice(0, dotIndex);

  const allowedLength = maxLength - ext.length - 4;
  return base.length > allowedLength ? base.slice(0, allowedLength) + "..." + ext : name;
};

/**
 * Calculate age in Japanese way: birthday must have passed this year to add one year
 * (e.g. born Feb 10 1990 → 35 until Feb 10, then 36)
 */
export const getAgeJapanese = (birthDateStr: string): number | null => {
  if (!birthDateStr || !birthDateStr.trim()) return null;
  const birth = dayjs(birthDateStr);
  if (!birth.isValid()) return null;
  const today = dayjs();
  let age = today.year() - birth.year();
  const hasHadBirthdayThisYear =
    today.month() > birth.month() || (today.month() === birth.month() && today.date() >= birth.date());
  if (!hasHadBirthdayThisYear) age -= 1;
  if (age < 0) return null;
  return age;
};

export const detectType = (char: string) => {
  if (/[a-zA-Z]/.test(char)) return "romaji";
  if (/[\u3040-\u309F]/.test(char)) return "hiragana";
  if (/[ｦ-ﾟ]/.test(char)) return "half-kana";
  if (/[ァ-ヶー]/.test(char)) return "full-kana";
  if (/[\u4E00-\u9FFF]/.test(char)) return "kanji";
  return "other";
};

export const splitByCharType = (input: string) => {
  const result: string[] = [];
  let buffer = "";
  let lastType = null;

  for (const ch of input) {
    const type = detectType(ch);
    if (type !== lastType && buffer) {
      result.push(buffer);
      buffer = "";
    }
    buffer += ch;
    lastType = type;
  }

  if (buffer) result.push(buffer);
  return result;
};

export const validateMobilePhone = () => ({
  validator: (_: unknown, value: string) => {
    if (!value) {
      return Promise.resolve();
    }

    if (!/^[0-9-]*$/.test(value)) {
      return Promise.reject("数字のみで入力してください。");
    }

    const numberCount = (value.match(/\d/g) || []).length;
    const hyphenCount = (value.match(/-/g) || []).length;
    if (numberCount > 11 || hyphenCount > 2) {
      return Promise.reject("11桁以内、ハイフン（-）は2つまでで入力してください。");
    }

    return Promise.resolve();
  }
});
